import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, TextField, MenuItem, Button, Alert, Snackbar,
  Grid, Table, TableBody, TableCell, TableHead, TableRow, Chip, CircularProgress, Card, CardContent
} from '@mui/material';
import { eledgerService } from '../services/eledgerService';
import { transferService } from '../services/transferService';

function Transfers() {
  const [ledgers, setLedgers] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const [formData, setFormData] = useState({
    from_e_ledger_id: '',
    to_e_ledger_id: '',
    transfer_date: new Date().toISOString().split('T')[0],
    product_name: '',
    quantity: '',
    quantity_unit_id: 'units',
    transferred_liability_kg_co2e: '',
    transfer_type: 'sale',
    legal_transfer_reference: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [ledgerData, transferData] = await Promise.all([
        eledgerService.getAll(),
        transferService.getAll()
      ]);
      setLedgers(ledgerData ?? []);
      setTransfers(transferData ?? []);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await transferService.initiate(formData);
      setSnackbar({ open: true, message: 'E-ledger transfer initiated', severity: 'success' });
      setFormData({
        ...formData,
        product_name: '',
        quantity: '',
        transferred_liability_kg_co2e: '',
        legal_transfer_reference: ''
      });
      loadData();
    } catch (err) {
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.error?.message || 'Failed to initiate transfer', 
        severity: 'error' 
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async (id, status) => {
    try {
      await transferService.verify(id, status);
      setSnackbar({ open: true, message: `Transfer ${status}`, severity: 'success' });
      loadData();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to verify transfer', severity: 'error' });
    }
  };

  const selectedFromLedger = ledgers.find(l => l.e_ledger_id === formData.from_e_ledger_id);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>E-Ledger Transfers</Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Transfer emissions with legal economic transfers (requires invoice/contract reference)
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Initiate Transfer</Typography>
            <form onSubmit={handleSubmit}>
              <TextField
                select
                label="From E-Ledger"
                fullWidth
                margin="normal"
                required
                value={formData.from_e_ledger_id}
                onChange={(e) => setFormData({ ...formData, from_e_ledger_id: e.target.value })}
              >
                {ledgers.filter(l => l.status === 'active').map((l) => (
                  <MenuItem key={l.e_ledger_id} value={l.e_ledger_id}>
                    {l.ledger_name} (Balance: {parseFloat(l.balance_kg_co2e).toFixed(2)} kg)
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="To E-Ledger (optional for end consumer)"
                fullWidth
                margin="normal"
                value={formData.to_e_ledger_id}
                onChange={(e) => setFormData({ ...formData, to_e_ledger_id: e.target.value })}
              >
                <MenuItem value="">End Consumer (exits circulation)</MenuItem>
                {ledgers.filter(l => l.e_ledger_id !== formData.from_e_ledger_id).map((l) => (
                  <MenuItem key={l.e_ledger_id} value={l.e_ledger_id}>{l.ledger_name}</MenuItem>
                ))}
              </TextField>

              <TextField
                type="date"
                label="Transfer Date"
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
                value={formData.transfer_date}
                onChange={(e) => setFormData({ ...formData, transfer_date: e.target.value })}
              />

              <TextField
                label="Product Name"
                fullWidth
                margin="normal"
                value={formData.product_name}
                onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
              />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    type="number"
                    label="Quantity"
                    fullWidth
                    margin="normal"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Unit"
                    fullWidth
                    margin="normal"
                    value={formData.quantity_unit_id}
                    onChange={(e) => setFormData({ ...formData, quantity_unit_id: e.target.value })}
                  />
                </Grid>
              </Grid>

              <TextField
                type="number"
                label="Transferred Liability (kg CO2e)"
                fullWidth
                margin="normal"
                required
                value={formData.transferred_liability_kg_co2e}
                onChange={(e) => setFormData({ ...formData, transferred_liability_kg_co2e: e.target.value })}
                helperText={selectedFromLedger ? `Available: ${parseFloat(selectedFromLedger.balance_kg_co2e).toFixed(4)} kg CO2e` : ''}
              />

              <TextField
                select
                label="Transfer Type"
                fullWidth
                margin="normal"
                value={formData.transfer_type}
                onChange={(e) => setFormData({ ...formData, transfer_type: e.target.value })}
              >
                <MenuItem value="sale">Sale</MenuItem>
                <MenuItem value="consignment">Consignment</MenuItem>
                <MenuItem value="internal_transfer">Internal Transfer</MenuItem>
              </TextField>

              <TextField
                label="Legal Transfer Reference"
                fullWidth
                margin="normal"
                required
                value={formData.legal_transfer_reference}
                onChange={(e) => setFormData({ ...formData, legal_transfer_reference: e.target.value })}
                helperText="Invoice or contract number (REQUIRED per E-ledger principles)"
                error={formData.legal_transfer_reference === ''}
              />

              <Button 
                type="submit" 
                variant="contained" 
                sx={{ mt: 2 }} 
                disabled={submitting || !formData.from_e_ledger_id || !formData.legal_transfer_reference || !formData.transferred_liability_kg_co2e}
              >
                {submitting ? <CircularProgress size={24} /> : 'Initiate Transfer'}
              </Button>
            </form>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>E-Ledger Transfer Principle</Typography>
              <Alert severity="warning">
                E-ledgers can <strong>ONLY</strong> be removed from an entity's ledger upon <strong>legal economic transfer</strong> (sale, contract) of outputs. A legal reference (invoice/contract number) is mandatory.
              </Alert>
            </CardContent>
          </Card>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Transfer History</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>From</TableCell>
                  <TableCell>To</TableCell>
                  <TableCell align="right">kg CO2e</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transfers.map((transfer) => (
                  <TableRow key={transfer.transfer_id}>
                    <TableCell>{transfer.transfer_date}</TableCell>
                    <TableCell>{transfer.fromLedger?.ledger_name || 'N/A'}</TableCell>
                    <TableCell>{transfer.toLedger?.ledger_name || 'End Consumer'}</TableCell>
                    <TableCell align="right">{parseFloat(transfer.transferred_liability_kg_co2e).toFixed(2)}</TableCell>
                    <TableCell>{transfer.legal_transfer_reference}</TableCell>
                    <TableCell>
                      <Chip 
                        label={transfer.verification_status} 
                        size="small" 
                        color={transfer.verification_status === 'verified' ? 'success' : transfer.verification_status === 'pending' ? 'warning' : 'error'}
                      />
                    </TableCell>
                    <TableCell>
                      {transfer.verification_status === 'pending' && (
                        <>
                          <Button size="small" onClick={() => handleVerify(transfer.transfer_id, 'verified')}>Verify</Button>
                          <Button size="small" color="error" onClick={() => handleVerify(transfer.transfer_id, 'disputed')}>Dispute</Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default Transfers;
